import { Button } from "./button";
import { ChevronLeft, ChevronRight } from "lucide-react";

type PaginationControlProps = {
  page: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  pageSizeOptions?: number[];
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
};

export const PaginationControls = ({
  page,
  totalPages,
  totalItems,
  itemsPerPage: pageSize,
  pageSizeOptions = [10, 20, 50, 100],
  onPageChange,
  onPageSizeChange,
}: PaginationControlProps) => {
  const hasItems = totalItems > 0;
  const start = hasItems ? (page - 1) * pageSize + 1 : 0;
  const end = hasItems ? Math.min(page * pageSize, totalItems) : 0;
  const showNavigation = totalPages > 1;

  return (
    <div className="bg-card/50 px-3 sm:px-4 py-3 rounded-lg">
      <div className="flex flex-col gap-3 sm:gap-4 lg:flex-row lg:items-center lg:justify-between">
        {/* Informations et sélecteur */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 text-sm text-muted-foreground">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-foreground">
              {hasItems
                ? `Affichage de ${start} à ${end}`
                : "Aucun élément à afficher"}
            </span>
            <span className="hidden sm:inline-block text-border">•</span>
            <span className="whitespace-nowrap">Total : {totalItems}</span>
          </div>

          {onPageSizeChange && (
            <div className="flex items-center gap-2 rounded-md border border-input bg-background px-2 py-1 text-sm w-full sm:w-auto">
              <span className="text-muted-foreground whitespace-nowrap">
                Par page
              </span>
              <select
                className="bg-transparent text-foreground focus:outline-none flex-1 sm:flex-none min-w-0"
                value={pageSize}
                onChange={(event) =>
                  onPageSizeChange(Number(event.target.value))
                }
              >
                {pageSizeOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Navigation */}
        {showNavigation && (
          <div className="flex items-center justify-between sm:justify-center gap-2 sm:gap-3">
            <Button
              variant="outline"
              size="sm"
              className="gap-1 sm:gap-2 flex-1 sm:flex-none"
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1}
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Précédent</span>
            </Button>

            <span className="text-xs sm:text-sm text-muted-foreground whitespace-nowrap px-2">
              Page <span className="font-semibold text-foreground">{page}</span>
              {" / "}
              {totalPages}
            </span>

            <Button
              variant="outline"
              size="sm"
              className="gap-1 sm:gap-2 flex-1 sm:flex-none"
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages}
            >
              <span className="hidden sm:inline">Suivant</span>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
